import React, { useEffect } from 'react';
import { EventBus } from '../../../app/contexts/event-bus';
import { useFlightWorker } from '../../../app/hooks/use-flight-worker';
import commonFormatter from '../../../formatters/common-formatter';
import useIntegration, { IntegrationAction } from '../../../hooks/use-integration';
import useMapInteraction from '../../../hooks/use-map-interaction';
import { EditOutline } from '../../../icons/icons';
import { MessageType } from '../../../services/actor-messages';
import { FpmRouteAction, sendRouteToFpm } from '../../../services/integration/fpm-request-tracker';
import { RouteAnalyzerContextMenuData } from '../../../services/map-interaction-service';
import { Menu, MenuItem } from '../../common/menu/DropdownMenu';
import useRouteAnalyzerLayer from '../../hooks/useRouteAnalyzerLayer';
import { AnalyzerRoute } from '../../route-analyzer/types';

const fpmActions: { action: FpmRouteAction; label: string; right: IntegrationAction }[] = [
    { action: 'calculate', label: 'Calculate', right: 'canCalcRouteInFPM' },
    { action: 'verify', label: 'Verify', right: 'canVerifyRouteInFPM' }
];

export function RouteAnalyzerContextMenu(props: { info: RouteAnalyzerContextMenuData }) {
    const mapInteraction = useMapInteraction();
    const context = props.info;
    const layer = useRouteAnalyzerLayer();
    const [routes, setRoutes] = React.useState<AnalyzerRoute[]>([]);
    // null while loading
    const [nonActiveFlightIds, setNonActiveFlightIds] = React.useState<number[] | null>(null);
    const integrationInfo = useIntegration();
    const worker = useFlightWorker();

    useEffect(() => {
        if (!context) {
            return;
        }

        const routes = layer!.getByIds(context.routes) ?? [];
        setRoutes(routes);
        setNonActiveFlightIds(null);
        worker.actor
            .sendAsync({
                type: MessageType.getNotActive,
                data: routes.flatMap((r) => (r.flightId !== undefined ? [r.flightId] : []))
            })
            .then(setNonActiveFlightIds);
    }, [worker, context?.routes, mapInteraction, layer]);

    const handleEdit = (route: AnalyzerRoute) => {
        EventBus.fire('route-analyzer-edit-start', { id: route.id });
        mapInteraction.dismissContextMenu();
    };

    const handleSendToFpm = (action: FpmRouteAction, route: AnalyzerRoute) => {
        sendRouteToFpm(action, route);
        mapInteraction.dismissContextMenu();
    };

    // Routes can only be calculated / verified for flights that are not airborne yet
    const getRouteDisabledReason = (route: AnalyzerRoute) => {
        if (route.flightId === undefined) {
            return 'Not linked to a flight';
        }
        if (nonActiveFlightIds === null) {
            return 'Checking flight status…';
        }
        if (!nonActiveFlightIds.includes(route.flightId)) {
            return 'Flight is already airborne or no longer available';
        }
        return null;
    };

    return (
        <>
            <Menu label="Edit graphically" icon={EditOutline}>
                {routes.map((route) => (
                    <MenuItem key={route.id} onClick={() => handleEdit(route)} labelElement={<RouteLabel route={route} />} />
                ))}
            </Menu>
            {integrationInfo.enabled &&
                fpmActions.map(({ action, label, right }) => {
                    const integrationReason = integrationInfo.getDisabledReason(right);
                    return (
                        <Menu key={action} label={`${label} route in ${integrationInfo.typeText}`}>
                            {routes.map((route) => {
                                const reason = integrationReason ?? getRouteDisabledReason(route);
                                return (
                                    <MenuItem
                                        key={route.id}
                                        disabled={!!reason}
                                        hint={reason}
                                        onClick={() => handleSendToFpm(action, route)}
                                        labelElement={<RouteLabel route={route} />}
                                    />
                                );
                            })}
                        </Menu>
                    );
                })}
        </>
    );
}

function RouteLabel({ route }: { route: AnalyzerRoute }) {
    return (
        <div className="d-flex align-items-center gap-2">
            <div
                className="d-inline-block"
                style={{
                    aspectRatio: '1/1',
                    height: 15,
                    backgroundColor: route.color,
                    border: '1px solid rgba(0,0,0,0.33)'
                }}></div>
            <span>{route.label}</span>
            <span className="secondary-text" style={{ fontSize: 11 }}>
                {commonFormatter.formatFeedId(route.feedId)}
            </span>
        </div>
    );
}
